<script lang="ts" setup> 
    import { isBoolean } from 'lodash';
    import { onBeforeMount, onMounted, onUnmounted, ref, watch, computed, nextTick } from 'vue';
    import { ValidationState} from "../lib/Types/Types";
    import { HtmlChangeEventType, TextInputType, KeyboardEventType, IconTypes, HoverCursorTypes, DisplayStyle } from "../Enums/UiEnums"
    import { ValidationResults } from '../lib/Models/ValidationResults'
    import * as Sound from '../lib/Sound';
    import { hasSlot } from '../lib/VueHelpers'
    import { Alignment, ProductType} from '../Enums/ChoiceEnums';
    import * as Notification from '../lib/Notification' 
    import { timeout } from '../lib/ChoiceUtils';
    import { useSpeechToText } from '../lib/Composables/SpeechToText';
    import { useCompanySettingsStore } from '../lib/Stores/CompanySettingsStore';
    import { initPinia } from '../lib/PiniaMgr';
    import { AutoCompleteTypeValues } from '../lib/Types/AutoCompleteType';
    import Icon from './Icon.vue';

    type textValueTypes = string|number|undefined;
    
    interface INewRawTextValue {
        newText?: string,
        positionCaret?: number,
        wasModified: boolean
    }
    
    const props = withDefaults(defineProps<{
        textValue?: string | number ,
        maxLength? : number,
        disabled?: boolean,
        //mask?: string,    // Add mark -- high priority
        cssFramework?: "bootstrap" | "vanilla" | "search",
        required?: boolean,
        placeholder?: string,
        name?: string,
        alignment?: Alignment,
        rows?: number,
        errorDisplay?: boolean,
        inputType?: TextInputType,
        title?: string,
        autoCompleteType?: AutoCompleteTypeValues,
        suffixIcon?: IconTypes,
        suffixTitle?: string,
        leftAddOnDisplayStyle?: DisplayStyle,
        hideLeftAddOn?: boolean,
        rightAddOnDisplayStyle?: DisplayStyle,
        hideRightAddOn?: boolean,
        hideTranscribe?: boolean,
        speechToText?: boolean,
        extraClass?: string,
        formatter?: (value: any, hasFocus? : boolean) => string|undefined,
        normalizer?: (rawText: string|undefined, evt?: HtmlChangeEventType) => any,
        validator?: (normalizedValue: any, evt?: HtmlChangeEventType) => boolean | ValidationResults,
        acceptCharacter?: (char: string, rawvalue: string|undefined) => boolean
    }>(), {
        cssFramework: "bootstrap",
        required: false,
        alignment: Alignment.left,
        errorDisplay: true,
        inputType: TextInputType.text,
        hideTranscribe: false,
        speechToText: false
    });
    
    const emit = defineEmits<{
        (e: "update:textValue", strValue: textValueTypes): void,
        (e: "change:textValue", strValue: textValueTypes): void,
        (e: "paste:textValue", strValue: textValueTypes): void,
        (e: "keypress:textValue", strValue: textValueTypes): void,
        (e: "validationState", state: ValidationState, msg?: string): void,
        (e: "keydown", event: KeyboardEvent): void,
        (e: "focus", event: FocusEvent): void,
        (e: "blur", event: FocusEvent): void,
        (e: "suffixIconClicked"): void
    }>();

    defineExpose({focus, scrollIntoView, performValidation});
    
    const hasFocus = ref<boolean>(false);
    const numericFormatter = ref<boolean>(false);
    const rawValue = ref<string>();
    const isValid = ref<boolean>(); 
    const validationErrorMsg = ref<string>();
    const badCharacter = ref(false);
    const textInput = ref<HTMLInputElement>();
    const textArea = ref<HTMLTextAreaElement>();
    const transcribeButton = ref<HTMLButtonElement>();
    const speechToTextProductEnabled = ref<boolean|undefined>(false)
    const requiredText = "Required";
    let textBefore: string
    let textAfter: string
    const companySettingsStore = useCompanySettingsStore(initPinia())
    const {startAudioRecording, stopAudioRecording, transcription, transcriptionError, isTranscribing, audioAllowed} = useSpeechToText()

    watch(() => props.textValue, (_new, _old) => {
        if (_new !== _old) {
            rawValue.value = formatValue(_new?.toString());
            performValidation();
        }
    });
    watch(transcription, async newValue => await updateWithSpeech(newValue))

    const hasLeftAddon = computed(() => hasSlot("LeftAddon"));
    const hasRightAddon = computed(() => hasSlot("RightAddon")); 
    const hasBeforeLeftAddon = computed(() => hasSlot("BeforeLeftAddon")); 
    const hasBeforeRightAddon = computed(() => hasSlot("BeforeRightAddon")); 
    const isTextArea = computed(()=> (props.rows ?? 0) > 0); 
    const addonCssClass = computed<string>(() => props.cssFramework === "bootstrap" ? "input-group-text" : "small_addon");
    const leftAddonCssClass = computed<(string|undefined)[]>(() => [props.cssFramework === "bootstrap" ? "input-group-text" : "small_addon", props.leftAddOnDisplayStyle]);
    const rightAddonCssClass = computed<(string|undefined)[]>(() => [props.cssFramework === "bootstrap" ? "input-group-text" : "small_addon", props.rightAddOnDisplayStyle]);
    const isBootstrap = computed<boolean>(() => props.cssFramework === "bootstrap"); 
    const normalizedValue = computed(()=> props.normalizer ? props.normalizer(rawValue.value) : rawValue.value);
    const isSpeechToTextActive = computed(() => props.speechToText && audioAllowed.value && speechToTextProductEnabled.value)
    const cssClass = computed<string>(() =>
    { 
        if (isBootstrap.value) {
            let contentClass;
            if (Alignment.right === props.alignment) contentClass ="text-end";
            if (Alignment.center === props.alignment) contentClass = "text-center";
            contentClass = "text-start";
            if(props?.extraClass){
                contentClass = `${contentClass} ${props.extraClass}`;
            }
            return contentClass;
        }
        else { 
            const widthClass = (hasLeftAddon.value || hasRightAddon.value ? "TextAddon" : "Text100");
            const otherClass = (props.cssFramework == "search" ? "small_text_other_search" : "small_text_other")
            if (Alignment.right === props.alignment) return `small_text_end ${otherClass} ${widthClass}`;
            if (Alignment.center === props.alignment) return `small_text_center ${otherClass} ${widthClass}`;
            return `small_text_start ${otherClass} ${widthClass}` ;
        }
    })
    const inputCssClasses = computed(() =>{
        return [
            isBootstrap.value ? "form-control": "",
            false === isValid.value ? "Invalid" : "",
            badCharacter.value ? "BadCharacterAnimation" : "",
            cssClass.value 
        ];
    });

    onMounted(() => {
        if (props.formatter) {
            if (props.formatter.toString().indexOf("numberFormatter") != -1) {
                numericFormatter.value = true;
            }
        }
        rawValue.value = formatValue(props.textValue?.toString());

        if (rawValue.value && rawValue.value.length > 0)
        {
            performValidation();
            return;
        }

        // Since we don't have any data yet, indicate we're invalid at the start
        if (props.required)
        {
            emit("validationState", false, requiredText);
        }

    });
    onUnmounted(() => {
        emit("validationState", "unmounted");
    });
    onBeforeMount(async () => {
        if (isTextArea.value){
            speechToTextProductEnabled.value = await companySettingsStore.isProductActive(ProductType.SpeechToText, handleProductError)
        }
    })

    function emitValue(eventType: HtmlChangeEventType)
    { 
        rawValue.value = formatValue(rawValue.value);
         
        performValidation(eventType);
        
        switch (eventType)
        {
            case HtmlChangeEventType.input:
                emit("update:textValue", normalizedValue.value);
                break;

            case HtmlChangeEventType.change:
                emit("change:textValue", normalizedValue.value);
                break;

            case HtmlChangeEventType.paste:
                emit("update:textValue", normalizedValue.value);
                emit("paste:textValue", normalizedValue.value);
                emit("change:textValue", normalizedValue.value);
                break;
            
            case HtmlChangeEventType.keypress:
                emit("keypress:textValue", normalizedValue.value);
                break;

            default:
            alert("Unhandled HtmlChangeEventType: " + eventType);
            break;
        }
    }

    function focus(selectText: boolean = true)
    {
        const input = isTextArea.value ? textArea.value : textInput.value;
        input?.focus();
        if (selectText)
            input?.select();
    }

    function scrollIntoView(arg?: boolean | ScrollIntoViewOptions)
    {
        const input = isTextArea.value ? textArea.value : textInput.value;
        input?.scrollIntoView(arg);
    }

    function performValidation(eventType?: HtmlChangeEventType)
    {
        let isValidLocal: boolean = true;
        let validationErrorMsgLocal: string|undefined;

        if (props.validator)
        {            
            const validationResult = props.validator(normalizedValue.value, eventType);
            if (isBoolean(validationResult))
            {
                isValidLocal = validationResult;
            }
            else
            {                
                isValidLocal = validationResult.isValid;
                validationErrorMsgLocal = validationResult.errorMsg;
            }
        }

        // Make sure we check if we have any data!        
        if (isValidLocal && props.required)
        {
            if (undefined === normalizedValue.value || 0 === normalizedValue.value.length)
            {
                isValidLocal = false;
                validationErrorMsgLocal = requiredText;
            }
        }        

        // Indicate if we're in a valid state or not 
        isValid.value = isValidLocal;
        validationErrorMsg.value = validationErrorMsgLocal;
        emit("validationState", isValid.value, validationErrorMsg.value); 
    }

    // Inspect the pasted text and remove blocked characters
    async function pasteValue(pasteEvt: ClipboardEvent)
    {
        // Don't let the text get into the text box!!
        pasteEvt.preventDefault();
        /*
            Per Paul W.: At a later date we might consider using a call back (dynamic? /if available) instead of this code.
        */

        let newTextInfo = generateNewRawValueFromPastedData(pasteEvt);
        if (!newTextInfo.wasModified)
            return;

        // Make sure we won't overrun the max length
        if (props.maxLength && undefined !== newTextInfo.newText)
        {
            if (newTextInfo.newText.length > props.maxLength) {
                const result = await Notification.confirmDialog("Your clipboard value is larger than this field allows, do you wish to truncate it?",{titleHTML: "Pasting Issue", ConfirmText: "Truncate Value", RejectText: "Do Not Paste", modalStyle: Notification.ModalStyles.danger });
                if (!result) {
                    return;
                }
                newTextInfo.newText = newTextInfo.newText.substring(0, props.maxLength);
            }
        }

        rawValue.value = newTextInfo.newText;
        emitValue(HtmlChangeEventType.paste);

        // Reposition the selection?
        if (undefined !== newTextInfo.positionCaret)
        {
            // Wait for the control to update
            await nextTick();

            // Set selection
            (pasteEvt.currentTarget as HTMLInputElement).setSelectionRange(newTextInfo.positionCaret, newTextInfo.positionCaret);
        }
    }

    // Generate the new 'raw' text that we expect to update the input box with
    //  Do not update it yet!!  Just generate it.
    function generateNewRawValueFromPastedData(pasteEvt: ClipboardEvent): INewRawTextValue
    {
        let pastedText = pasteEvt.clipboardData?.getData('text/plain');
        if (!pastedText || 0 == pastedText.length)
            return { wasModified: false };

        let cleanPastedText : string|undefined = "";
        for(const char of pastedText)
        {
            if (!BlockedCharacter(char))
                cleanPastedText += char;
        }
        let initialNewRawValue = rawValue.value;

        // See if any text is selected and replace the selection/caret.
        const selStart = ((pasteEvt.currentTarget) as HTMLInputElement).selectionStart;
        const selEnd = ((pasteEvt.currentTarget) as HTMLInputElement).selectionEnd;
        if (null != selStart || null != selEnd)
        {
            const oldLeftPart = rawValue.value?.substring(0, selStart as number) ?? "";
            const oldRightPart = rawValue.value?.substring(selEnd as number) ?? "";
            initialNewRawValue = oldLeftPart + cleanPastedText + oldRightPart;
        }

        const newRawValue = formatValue(initialNewRawValue);

        if (newRawValue === rawValue.value)
            return { wasModified: false };

        // We can't predict where to put the caret if the value is reformatted
        if (initialNewRawValue != newRawValue)
        {
            return { 
                newText: newRawValue, 
                wasModified: true    
            };
        }

        if (undefined === newRawValue || null === selStart)
            return { newText: newRawValue, wasModified: false };

        // Where should the current selection be?
        const positionCaret = (undefined !== initialNewRawValue) ? selStart + cleanPastedText.length : undefined;

        return { 
            newText: newRawValue, 
            positionCaret: positionCaret,
            wasModified: true    
         };
    }

    function keyPress(evt: KeyboardEvent)
    {
        // Block pipes or other non-allowed keys
        if (BlockedCharacter(evt.key))
        {
            evt.preventDefault();
            alertUserToBadCharacter();
        }
        else {
            emitValue(HtmlChangeEventType.keypress);
        }
    }

    function keyDown(evt: KeyboardEvent)
    {
        emit("keydown", evt);
    }

    function BlockedCharacter(keyCodeOrCharacter: string|undefined): boolean
    {
        if (!keyCodeOrCharacter)
            return false;

        if ( (KeyboardEventType.backspace == keyCodeOrCharacter.toLowerCase()) || (KeyboardEventType.delete == keyCodeOrCharacter.toLowerCase()))
            return false;

        if ("|" === keyCodeOrCharacter) 
            return true;

        // Non-printable characters
        if (keyCodeOrCharacter.charCodeAt(0) < 10)
            return true;

        if (props.acceptCharacter)
            if (false === props.acceptCharacter(keyCodeOrCharacter, rawValue.value))
                return true;

        return false;
    }

    function formatValue(value: string | undefined): string|undefined
    {
        if (!props.formatter || !props.normalizer)
            return value;

        const normValue = props.normalizer(value);
        if (undefined === normValue)
            return value;
        return props.formatter(normValue,hasFocus.value);
    }

    async function alertUserToBadCharacter()
    {
        if (!badCharacter.value)
        {
            badCharacter.value = true;
            await timeout(250)
            badCharacter.value = false; 
        }
        Sound.playSound(Sound.errorSoundBase64);
    }


    function onBlur(evt: FocusEvent)
    {
        hasFocus.value = false;
        emit('blur', evt);
        if (numericFormatter.value) {
            emitValue(HtmlChangeEventType.change);
        }
    }
    
    function onFocus(evt: FocusEvent){   
        hasFocus.value = true;
        emit('focus', evt);
    }   
    
    function handleProductError(response: Response, tempErrorMessage?:string){
        if (response.status === 401){
            console.warn("User is not authorized for speech to text. Speech to text will not be enabled. This message is most likely resulting from an invalid cookie.")
            return
        }
        alert(`Error: ${tempErrorMessage ?? ""}`)
    }

    async function updateWithSpeech(speech: string){
        const newValue = textBefore + speech + textAfter
        if (props.maxLength && (newValue.length > props.maxLength)){
            stopAudioRecording()
            await Notification.popAlert("The last transcription would exceed the allowable length for this field. The field will not be updated.","Maximum Length Exceeded")
            return
        }
        rawValue.value = newValue 
        emitValue(HtmlChangeEventType.input)
    }

    function toggleTranscribing(){
        if (!isTranscribing.value){
            const area = textArea.value
            if (!area) return
            textBefore = area.value.substring(0, area.selectionStart)
            textAfter = area.value.substring(area.selectionEnd)
            startAudioRecording()
        } else {
            stopAudioRecording()
        }
    }

    const inputGroupClass = computed<(string|undefined)[]>(() =>{
        return [(hasLeftAddon || hasRightAddon) ? 'input-group' : '', "text-input-border"];
    });
</script>

<style scoped>
    .Invalid{border: 2px solid red }
    .ErrorMsg{ color:crimson}

    .BadCharacterAnimation {
        animation: flashRed 0.25s;
        border: 2px solid red;
      }
      
      @keyframes flashRed {
        from {background-color: rgb(244, 92, 92);}
        to {background-color: white;}
      }
    input.Text100 {
        width: 100%;
    }  
    input.TextAddon {
        width: 70%;
    }
    textarea.Text100 {
        width: 100%;
    }  
    textarea.TextAddon {
        width: 70%;
    }
    ::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
        color: SILVER;
	    FONT-STYLE: italic;
        text-align: left;
        opacity: 1; /* Firefox */
     }
     ::-webkit-input-placeholder {
        color: SILVER;
	    FONT-STYLE: italic;
        text-align: left;
     }
</style>
<style>
    .small_text_end {
        display: block;
        font-size: .75rem;
        font-weight: 400;
        line-height: 1rem;
        text-align: right;
        color: #212529;
        background-color: #fff;
        background-clip: padding-box;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        border-radius: 0.25rem;
        transition: border-color .15s ease-in-out,box-shadow .15s ease-in-out;
        position: relative;
        flex: 1 1 auto;
        min-width: 0;
    }
    .small_text_center {
        display: block;
        font-size: .75rem;
        font-weight: 400;
        line-height: 1rem;
        text-align: center;
        color: #212529;
        background-color: #fff;
        background-clip: padding-box;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        border-radius: 0.25rem;
        transition: border-color .15s ease-in-out,box-shadow .15s ease-in-out;
        position: relative;
        flex: 1 1 auto;
        min-width: 0;
    }
    .small_text_start {
        display: block;
        font-size: .75rem;
        font-weight: 400;
        line-height: 1rem;
        text-align: left;
        color: #212529;
        background-color: #fff;
        background-clip: padding-box;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        border-radius: 0.25rem;
        transition: border-color .15s ease-in-out,box-shadow .15s ease-in-out; 
        position: relative;
        flex: 1 1 auto;
        min-width: 0;
    }
    .small_text_other {
        padding: 0.375rem 0.75rem;
        border: 1px solid #ced4da;
    }
    .small_text_other_search {
        padding: 0.450rem 0.75rem;
        border: 1px solid #0d6efd;
    }
    span.small_addon { 
        display: flex;
        align-items: center;
        padding: 0.25rem 0.75rem;
        font-size: .75rem;
        font-weight: 400;
        line-height: 1.5;
        color: #212529;
        text-align: center;
        white-space: nowrap;
        background-color: #e9ecef;
        border: 1px solid #ced4da;
        border-radius: 0.25rem;
    }

    .input-suffix-icon {
        position: absolute; 
        opacity: 0.75;
        right: 0.5em;
        top: 0.75em;
    }

    .textarea-button {
        position: absolute; 
        opacity: 0.1;
        transition: opacity 0.3s;
        width:31px;
        right:20px;
        top:5px;
    }
    .textarea-button:hover {
        opacity: 1;
    }

    .textarea-button:focus {
        outline: none;
        box-shadow: none;
    }

    .transcribing {
        opacity: 1;
    }

    .input-group-text.danger {
        background-color: #dc3545;
        color: white;        
    }
    .input-group-text.warning {
        background-color: goldenrod;
        color: black;        
    }
    .input-group-text.primary {
        background-color: dodgerblue;
        color: white;        
    }
    .input-group-text.success {
        background-color: green;
        color: white;        
    }

</style>

<template>
    <div :class="inputGroupClass" :title="title" style="position:relative">
        <template v-if="hasBeforeLeftAddon">
            <slot name="BeforeLeftAddon" />
        </template>        
        <template v-if="hasLeftAddon && !hideLeftAddOn">
            <span :class="leftAddonCssClass">
                <slot name="LeftAddon" />
            </span>
        </template>
        <template v-if="!isTextArea">
            <input
                ref="textInput"
                v-model="rawValue"
                :class="inputCssClasses"
                :type="props.inputType ?? TextInputType.text" 
                :maxlength="props.maxLength ? props.maxLength : undefined" 
                :disabled="props.disabled" 
                :placeholder="placeholder"
                :name="name"
                :required="props.required"
                :autocomplete="props.autoCompleteType"
                @input="() => emitValue(HtmlChangeEventType.input)"
                @change="() => emitValue(HtmlChangeEventType.change)"
                @paste="(pasteEvt:ClipboardEvent) => pasteValue(pasteEvt)"
                @keypress="(evt: KeyboardEvent) => keyPress(evt)"
                @keydown="(evt: KeyboardEvent) => keyDown(evt)"
                @blur="onBlur"
                @focus="onFocus" />  

            <!-- Float an icon within the input on the far right -->
            <Icon v-if="suffixIcon" class="input-suffix-icon" :icon="props.suffixIcon" @click="emit('suffixIconClicked')" :title="props.suffixTitle" :hoverCursor="HoverCursorTypes.Pointer" />
        </template>
        <template v-if="isTextArea">
                <textarea
                    ref="textArea"
                    v-model="rawValue"
                    :class="inputCssClasses"
                    :rows="props.rows"
                    :maxlength="props.maxLength ? props.maxLength : undefined" 
                    :disabled="props.disabled" 
                    :placeholder="placeholder"
                    :name="name"
                    :required="props.required"
                    @input="() => emitValue(HtmlChangeEventType.input)"
                    @change="() => emitValue(HtmlChangeEventType.change)"
                    @paste="(pasteEvt:ClipboardEvent) => pasteValue(pasteEvt)"
                    @keypress="(evt: KeyboardEvent) => keyPress(evt)"
                    @keydown="(evt: KeyboardEvent) => keyDown(evt)"
                    @blur="onBlur"
                    @focus="onFocus"
                />
                <div class="alert-danger" v-if="transcriptionError.length > 0" style="position:absolute; bottom:0; padding: 0 5px;">{{transcriptionError}}</div>
                <button
                    ref="transcribeButton"
                    class="textarea-button btn btn-primary btn-sm"
                    @click="toggleTranscribing"
                    v-if="isSpeechToTextActive && !props.hideTranscribe"
                    :class="{'transcribing': isTranscribing}"
                    >
                    <i class="fas fa-pencil-alt" v-if="!isTranscribing"></i>
                    <i class="fas fa-stop" v-if="isTranscribing"></i>
                </button>
        </template>

        <!-- Use for things like buttons -->
        <template v-if="hasBeforeRightAddon">
            <slot name="BeforeRightAddon" />
        </template>

        <template v-if="hasRightAddon && !hideRightAddOn">
            <span :class="rightAddonCssClass">
                <slot name="RightAddon" />
            </span>
        </template>        
    </div>
    <div v-if="validationErrorMsg && props.errorDisplay" class="ErrorMsg">
        {{validationErrorMsg}}
    </div>
</template>

    
